import { ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Input, Popconfirm, Space, Table, message } from 'antd';
import { useCallback, useState } from 'react';
import AnalysisModal from './components/AnalysisModal';
import DishFormModal from './components/DishFormModal';
import { getMenuColumns } from './components/MenuColumns';
import UploadMenuModal from './components/UploadMenuModal';
import { useAnalysisModal } from './hooks/useAnalysisModal';
import { useMenuActions } from './hooks/useMenuActions';
import { useMenuModal } from './hooks/useMenuModal';
import { useMenuTable } from './hooks/useMenuTable';

export const MenuPage = () => {
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const {
    pagination,
    loading: fetchLoading,
    data,
    refetch,
    handleTableChange,
    handleSearch,
  } = useMenuTable();

  const {
    loading: actionLoading,
    handleStatusChange,
    handleDelete,
    goToPairingDetail,
    regeneratePairing,
  } = useMenuActions(refetch);

  const {
    modalVisible,
    currentMenu,
    initialFormValues,
    showAddModal,
    showEditModal,
    closeModal,
  } = useMenuModal();

  const {
    analysisModalVisible,
    currentMenuItem,
    showAnalysisModal,
    closeAnalysisModal,
  } = useAnalysisModal();

  const columns = getMenuColumns({
    handleStatusChange,
    handleDelete,
    showEditModal,
    showAnalysisModal,
    loading: actionLoading,
    regeneratePairing,
  });

  const isTableLoading = fetchLoading || actionLoading;

  const showUploadModal = () => {
    setUploadModalVisible(true);
  };

  const closeUploadModal = () => {
    setUploadModalVisible(false);
  };

  const handleBatchDelete = useCallback(async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select at least one dish to delete');
      return;
    }
    const selectedRecords = data.filter((record) =>
      selectedRowKeys.includes(record.id),
    );
    await handleDelete(selectedRecords);
    setSelectedRowKeys([]);
  }, [selectedRowKeys, data, handleDelete]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  return (
    <div className="space-y-6 p-8">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">Menu Management</h1>
      </div>

      <div className="flex justify-between items-center">
        <Space>
          <Input.Search
            placeholder="Search name"
            allowClear
            onSearch={handleSearch}
            className="w-[300px]"
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={async () => {
              refetch();
            }}
            loading={isTableLoading}
          />
        </Space>
        <Space>
          <Button
            type="default"
            icon={<UploadOutlined />}
            onClick={showUploadModal}
          >
            Upload Menu PDF
          </Button>
          {selectedRowKeys.length > 0 && (
            <Popconfirm
              title="Delete selected dishes"
              description={`Are you sure you want to delete ${selectedRowKeys.length} dishes?`}
              onConfirm={handleBatchDelete}
              okText="Yes"
              cancelText="No"
              okType="danger"
            >
              <Button
                type="primary"
                danger
                disabled={selectedRowKeys.length === 0}
                loading={actionLoading}
              >
                Delete Selected ({selectedRowKeys.length})
              </Button>
            </Popconfirm>
          )}
          <Button type="primary" onClick={showAddModal}>
            Add New Dish
          </Button>
        </Space>
      </div>

      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={isTableLoading}
        pagination={pagination}
        onChange={handleTableChange}
      />

      <DishFormModal
        visible={modalVisible}
        currentMenu={currentMenu}
        initialValues={initialFormValues}
        onSuccess={() => {
          refetch();
          closeModal();
        }}
        onCancel={closeModal}
      />

      <UploadMenuModal
        visible={uploadModalVisible}
        onCancel={closeUploadModal}
        onSuccess={() => {
          refetch();
          closeUploadModal();
        }}
      />

      <AnalysisModal
        visible={analysisModalVisible}
        menu={currentMenuItem}
        goToPairingDetail={goToPairingDetail}
        onClose={closeAnalysisModal}
      />
    </div>
  );
};
